// FILE: src/pages/attendance/Attendance.jsx
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useGlobalContext } from "../../GlobalContext";
import LoadingScreen from "../Utils/LoadingScreen";
import useAttendance from "../../Sevices/useAttendance";
import "./attendance.css";
import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";
import { useNavigate } from "react-router-dom";
import { FileDown, RefreshCcw, X } from "lucide-react";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";

const normalizeStatus = (s) => {
  if (!s) return "pending";

  const v = String(s).toLowerCase().trim();

  if (v === "f") return "full_day";
  if (v === "h") return "half_day";
  if (v === "a") return "absent";
  if (v === "p") return "pending";

  if (v.includes("full")) return "full_day";
  if (v.includes("half")) return "half_day";
  if (v.includes("absent")) return "absent";

  return "pending";
};

const STATUS_ORDER = ["pending", "full_day", "half_day", "absent"];

const STATUS_LABEL = {
  full_day: "F",
  half_day: "H",
  absent: "A",
  pending: "PN",
};

const STATUS_CLASS = {
  full_day: "bg-green-500 text-white",
  half_day: "bg-yellow-400 text-black",
  absent: "bg-red-500 text-white",
  pending: "bg-blue-500 text-white",
};

const TYPE_BADGE = {
  full_time: "bg-indigo-500/10 text-indigo-700 border-indigo-200",
  part_time: "bg-sky-500/10 text-sky-700 border-sky-200",
  normal: "bg-slate-500/10 text-slate-700 border-slate-200",
};

const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

const getMonthDays = (year, month) => {
  const days = new Date(year, month + 1, 0).getDate();
  return Array.from({ length: days }, (_, i) =>
    toYMD(new Date(year, month, i + 1)),
  );
};

const formatDay = (ymd) => new Date(ymd).getDate();
const formatDayName = (ymd) => {
  return new Date(ymd).toLocaleDateString("en-US", {
    weekday: "short",
  });
};

export default function Attendance() {
  const { AdminId } = useGlobalContext();
  const isAdminReady = !!AdminId;

  const { fetchAttendanceApi, updateAttendanceApi } = useAttendance();

  const { drivers, loading, fetchDrivers } = useDriverAllApi();

  // month selection
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-index
  const dates = useMemo(() => getMonthDays(year, month), [year, month]);

  // drivers list (for dropdown)
  const [driverOptions, setDriverOptions] = useState([]);
  const [driverListLoading, setDriverListLoading] = useState(false);

  // UI inputs (not applied until Apply Filters)
  const [uiDriverCode, setUiDriverCode] = useState("");
  const [uiSearch, setUiSearch] = useState("");
  const [uiType, setUiType] = useState("all");
  const [uiStatus, setUiStatus] = useState("all");

  const todayStr = new Date().toISOString().split("T")[0];
  const [exportModal, setExportModal] = useState(false);
  const [exportFromDate, setExportFromDate] = useState(todayStr);
  const [exportToDate, setExportToDate] = useState(todayStr);

  // applied filters
  const [applied, setApplied] = useState({
    driverCode: "",
    search: "",
    type: "all",
    status: "all",
  });

  // data
  const [dirtyMap, setDirtyMap] = useState({}); // key = driver|date -> {driverCode,date,status}
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [attendanceDrivers, setAttendanceDrivers] = useState([]);
  const navigate = useNavigate();
  const canCallApi = !!AdminId;

  /*  FETCH DRIVER LIST  */
  useEffect(() => {
    if (!AdminId) return;

    fetchDrivers().then((list) => {
      if (Array.isArray(list)) {
        setDriverOptions(list);
      }
    });
  }, [AdminId]);

  const transformDrivers = (apiData = []) => {
    return apiData.map((d) => {
      const attendanceMap = {};

      (d.attendance || []).forEach((item) => {
        attendanceMap[item.date] = item.status;
      });

      return {
        ...d,
        attendanceMap,
      };
    });
  };

  /*  FETCH ATTENDANCE  */
  const fetchAttendance = async (driverCodeParam) => {
    fetchAttendanceApi(
      {
        driverCode: driverCodeParam || "",
        fromDate: dates[0],
        toDate: dates[dates.length - 1],
      },
      (data) => {
        const formatted = transformDrivers(data || []);
        setAttendanceDrivers(formatted);
      },
      setDirtyMap,
      setAttendanceLoading,
    );
  };

  useEffect(() => {
    if (!AdminId) return;
    if (!dates.length) return;

    fetchAttendance("");
  }, [AdminId, year, month, dates]);

  /*  APPLY FILTERS  */
  const onApplyFilters = () => {
    const next = {
      driverCode: uiDriverCode,
      search: uiSearch.trim(),
      type: uiType,
      status: uiStatus,
    };
    setApplied(next);
    fetchAttendance(next.driverCode);
  };

  const onResetFilters = () => {
    setUiDriverCode("");
    setUiSearch("");
    setUiType("all");
    setUiStatus("all");
    const next = { driverCode: "", search: "", type: "all", status: "all" };
    setApplied(next);
    fetchAttendance("");
  };

  /*  GRID INTERACTION  */
  const cycleStatus = (driverCode, date, current) => {
    const next =
      STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length];

    const key = `${driverCode}|${date}`;

    setDirtyMap((prev) => ({
      ...prev,
      [key]: { driverCode, date, status: next },
    }));
  };

  const convertStatusToApi = (status) => {
    if (status === "full_day") return "F";
    if (status === "half_day") return "H";
    if (status === "absent") return "A";
    return "P";
  };

  /* SAVE (Batch Update API)  */
  const saveAll = async () => {
    const data = Object.values(dirtyMap).map((item) => ({
      driverCode: item.driverCode,
      date: item.date,
      status: convertStatusToApi(item.status),
    }));

    if (!data.length) return toast.message("No changes to save");
    if (!canCallApi) return toast.error("Admin/session not ready");

    await updateAttendanceApi(data);

    setDirtyMap({});
    fetchAttendance(applied.driverCode);
  };

  const discardAll = () => {
    if (!Object.keys(dirtyMap).length) return;
    setDirtyMap({});
    fetchAttendance(applied.driverCode);
  };

  /*  CLIENT-SIDE FILTERING  */
  const filteredDrivers = useMemo(() => {
    let list = [...attendanceDrivers];

    if (uiType !== "all") {
      list = list.filter((d) => String(d.type || "").toLowerCase() === uiType);
    }

    if (uiSearch) {
      const q = uiSearch.toLowerCase();
      list = list.filter((d) => {
        const name = String(d.name || "")
          .toLowerCase()
          .trim();
        const code = String(d.driverCode || "").toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    }

    if (uiStatus !== "all") {
      list = list.filter((d) =>
        Object.values(d.attendanceMap || {}).some(
          (s) => normalizeStatus(s) === uiStatus,
        ),
      );
    }

    return list;
  }, [attendanceDrivers, uiSearch, uiType, uiStatus]);

  const dirtyCount = Object.keys(dirtyMap).length;

  const handleExportDownload = () => {
    if (!filteredDrivers.length) {
      toast.message("No data to export");
      return;
    }

    const exportData = filteredDrivers.map((d) => {
      const row = {
        DriverCode: d.driverCode,
        Name: d.name || "",
        Type: d.type || "",
      };

      dates
        .filter((date) => date >= exportFromDate && date <= exportToDate)
        .forEach((date) => {
          const status = normalizeStatus(d.attendanceMap?.[date]);
          row[`${formatDay(date)}-${formatDayName(date)}`] =
            STATUS_LABEL[status];
        });

      return row;
    });

    exportAnyJsonToExcel({
      data: exportData,
      filePrefix: `Attendance_${exportFromDate}_to_${exportToDate}`,
      sheetName: "Attendance",
    });

    setExportModal(false);
  };

  return (
    <div className="w-full overflow-x-hidden px-4 py-4">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Attendance</h1>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
            <div className="flex items-center gap-1">
              <span className="att-legend bg-emerald-500" /> F – Full
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-amber-400" /> H – Half
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-rose-500" /> A – Absent
            </div>
            <div className="flex items-center gap-1">
              <span className="att-legend bg-indigo-500" /> PN – Pending
            </div>
            <span className="text-slate-400">
              • Click badge to change (F → H → A → PN)
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* Refresh Button */}
          <button
            onClick={() => fetchAttendance(applied.driverCode)}
            disabled={attendanceLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-500 hover:bg-gray-600 text-white"
          >
            <RefreshCcw
              size={14}
              className={attendanceLoading ? "animate-spin" : ""}
            />
            <span>Refresh</span>
          </button>

          {/* Export Button */}
          <button
            onClick={() => {
              setExportFromDate(todayStr);
              setExportToDate(todayStr);
              setExportModal(true);
            }}
            className="flex items-center gap-2 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
          >
            <FileDown size={14} />
            <span>Export</span>
          </button>

          <button
            onClick={discardAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 disabled:opacity-60"
          >
            Discard ({dirtyCount})
          </button>

          <button
            onClick={saveAll}
            disabled={loading || dirtyCount === 0}
            className="px-3 py-2 rounded-xl bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
          >
            Save Changes ({dirtyCount})
          </button>
        </div>
      </div>

      {attendanceLoading ? (
        <div className="flex justify-center items-center py-20">
          <LoadingScreen />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 ">
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-2">
                  <label className="text-xs text-slate-500">Month</label>
                  <input
                    type="month"
                    value={`${year}-${String(month + 1).padStart(2, "0")}`}
                    onChange={(e) => {
                      const [y, m] = e.target.value.split("-");
                      setYear(+y);
                      setMonth(+m - 1);
                    }}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                  />
                </div>

                <div className="md:col-span-4">
                  <label className="text-xs text-slate-500">Driver</label>
                  <select
                    value={uiDriverCode}
                    onChange={(e) => setUiDriverCode(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                    disabled={driverListLoading}
                  >
                    <option value="">All Drivers</option>
                    {driverOptions.map((d) => (
                      <option key={d.driverId} value={d.driverId}>
                        {d.fullName || d.username || d.driverId} ({d.driverId})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-3">
                  <label className="text-xs text-slate-500">Search</label>
                  <input
                    value={uiSearch}
                    onChange={(e) => {
                      const value = e.target.value;
                      setUiSearch(value);

                      // optional: update applied state too if you still use it elsewhere
                      setApplied((prev) => ({
                        ...prev,
                        search: value.trim(),
                      }));
                    }}
                    placeholder="Search name or code..."
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200"
                  />
                </div>

                <div className="md:col-span-1">
                  <label className="text-xs text-slate-500">Type</label>
                  <select
                    value={uiType}
                    onChange={(e) => setUiType(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                  >
                    <option value="all">All</option>
                    <option value="full_time">Full-time</option>
                    <option value="part_time">Part-time</option>
                    <option value="normal">Normal</option>
                  </select>
                </div>

                <div className="md:col-span-1">
                  <label className="text-xs text-slate-500">Status</label>
                  <select
                    value={uiStatus}
                    onChange={(e) => setUiStatus(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-200 bg-white"
                  >
                    <option value="all">Any</option>
                    <option value="full_day">Full</option>
                    <option value="half_day">Half</option>
                    <option value="absent">Absent</option>
                  </select>
                </div>

                <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-2 mt-2">
                  <div className="flex gap-2">
                    <button
                      onClick={onApplyFilters}
                      disabled={loading || !canCallApi}
                      className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                    >
                      Apply Filters
                    </button>

                    <button
                      onClick={onResetFilters}
                      disabled={loading || !canCallApi}
                      className="px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
                    >
                      Reset
                    </button>
                  </div>

                  <div className="text-xs text-slate-500">
                    Showing{" "}
                    <span className="font-semibold text-slate-700">
                      {filteredDrivers.length}
                    </span>{" "}
                    drivers •{" "}
                    <span className="font-semibold text-slate-700">
                      {dates.length}
                    </span>{" "}
                    days
                  </div>
                </div>
              </div>
            </div>

            {/* Grid */}
            <div className="bg-white rounded-2xl border border-slate-200 mt-4">
              <div className="attendance-grid-scroll">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th className="attendance-th sticky-top sticky-driver text-left">
                        Partner
                      </th>
                      {dates.map((d) => (
                        <th
                          key={d}
                          className="bg-orange-400 attendance-th sticky-top text-center date-col"
                        >
                          <div className="flex flex-col items-center leading-tight">
                            <span className="text-[10px] font-medium">
                              {formatDayName(d)}
                            </span>
                            <span className="text-sm font-bold">
                              {formatDay(d)}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {filteredDrivers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={1 + dates.length}
                          className="py-10 text-center text-slate-500"
                        >
                          No drivers found for this filter/month.
                        </td>
                      </tr>
                    ) : null}

                    {filteredDrivers.map((d) => (
                      <tr key={d.driverCode} className="hover:bg-slate-50/70">
                        <td className=" attendance-td sticky-driver">
                          <div className="min-w-0">
                            <div
                              onClick={() => {
                                const driverId = d.driverCode;
                                const username = encodeURIComponent(
                                  d.name || d.driverCode,
                                );
                                const monthStart = `${year}-${String(month + 1).padStart(2, "0")}-01`;
                                const todayDate = new Date()
                                  .toISOString()
                                  .split("T")[0];

                                navigate(`/analytics/${driverId}/${username}`, {
                                  state: {
                                    fromDate: monthStart,
                                    toDate: todayDate,
                                  },
                                });
                              }}
                              className="text-[12px] font-semibold text-blue-600 cursor-pointer hover:underline truncate max-w-[150px]"
                            >
                              {d.name || "-"}
                            </div>
                            <div className="text-[10px] text-slate-500">
                              {d.driverCode}
                            </div>
                            <span
                              className={[
                                "inline-flex items-center mt-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold",
                                TYPE_BADGE[
                                String(d.type || "").toLowerCase()
                                ] || TYPE_BADGE.normal,
                              ].join(" ")}
                            >
                              {String(d.type || "normal").replaceAll("_", " ")}
                            </span>
                          </div>
                        </td>

                        {dates.map((date) => {
                          const key = `${d.driverCode}|${date}`;

                          const rawStatus =
                            dirtyMap[key]?.status || d.attendanceMap?.[date];

                          const status = normalizeStatus(rawStatus);

                          const isDirty = !!dirtyMap[key];

                          return (
                            <td
                              key={date}
                              className=" attendance-td text-center"
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  cycleStatus(d.driverCode, date, status)
                                }
                                className={[
                                  "att-badge",
                                  STATUS_CLASS[status],
                                  isDirty ? "ring-2 ring-slate-900/20" : "",
                                ].join(" ")}
                                title="Click to change"
                              >
                                {STATUS_LABEL[status]}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500">
                Driver column is fixed. Scroll inside grid to see all dates.
              </div>
              <div className="text-xs">
                {dirtyCount ? (
                  <span className="text-rose-600 font-semibold">
                    {dirtyCount} unsaved change(s)
                  </span>
                ) : (
                  <span className="text-emerald-600 font-semibold">
                    All changes saved
                  </span>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {exportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-96 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">
                Export Attendance
              </h3>
              <button
                onClick={() => setExportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                From Date
              </label>
              <input
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-600">
                To Date
              </label>
              <input
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setExportModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover: transition"
              >
                Cancel
              </button>
              <button
                onClick={handleExportDownload}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition"
              >
                <FileDown size={15} />
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
